import 'core-js';
import 'babel-polyfill';
import 'antd-mobile/es/global';
import { useEffect, useRef, useLayoutEffect } from 'react';
import { useRouter } from 'next/router';
import { appWithTranslation } from 'next-i18next';
import NextApp from 'next/app';
import * as Sentry from '@sentry/nextjs';
import StoreProvider from '@/components/GlobalProvider';
import efbridge from '@/lib/efbridge';
import { getLocalStorage, setLocalStorage, removeLocalStorage } from '@/utils';
import { appLogwebLoadMetrics } from '@/utils/bridge';
import { QueryParams, getAppCurrentLang, getAppToken, getQueryString, getRedirectPath, isJSON } from '@/utils/tools';
import '../styles/globals.scss';
import { notFoundConfig } from '@/constants/common';
import { EFAppMsgProps } from '@/types';
import { NEW_REQUEST_LANG_MAP } from '@/constants/common';

function EfApp({ Component, pageProps }: any) {
    const firstRenderRef = useRef(false);
    const router = useRouter();
    const { locale = 'us' } = router?.query;

    if (!firstRenderRef.current) {
        efbridge.init();
        const token = getAppToken();
        if (!token) {
            efbridge.registerHandler('getAppMsg', (res: EFAppMsgProps) => {
                if (res) {
                    const { lang, version, token, code } = res as EFAppMsgProps;
                    setLocalStorage('token', token);
                    setLocalStorage('version', version);
                    setLocalStorage('lang', lang);
                    setLocalStorage('code', code);
                }
            });
        }
        firstRenderRef.current = true;
    }

    const initAppLogwebLoadMetrics = () => {
        console.log('initAppLogwebLoadMetrics');
        const data = getLocalStorage('efmetric');
        const metric = typeof data === 'string' && isJSON(data) ? JSON.parse(data) : data;
        const { TTFB, FCP, LCP, FID, CLS } = metric || {};
        appLogwebLoadMetrics(
            JSON.stringify({
                page_url: router.asPath,
                h5_time_to_first_byte: TTFB || '',
                h5_first_contentful_paint: FCP || '',
                h5_largest_contentful_paint: LCP || '',
                h5_first_input_delay: FID || '',
                h5_cumulative_layout_shift: CLS || '',
            }),
        ).then((res: any) => {
            console.log('removeLocalStorage', res);
            removeLocalStorage('efmetric');
        });
    };

    // 根据语言切换路由
    const replaceLocale = (locale: string) => {
        const { pathname, query } = router;
        const params = getQueryString() as QueryParams;
        const newQuery = { ...query, ...params, locale };
        router.replace({ pathname, query: newQuery }, undefined, { locale });
    };

    useLayoutEffect(() => {
        const lang = getAppCurrentLang() as string;
        if (lang && lang !== locale && NEW_REQUEST_LANG_MAP[lang] !== locale) {
            const current_lang = NEW_REQUEST_LANG_MAP[lang] || lang;
            // 重置语言
            replaceLocale(current_lang);
        } else {
            if ((locale as string) && NEW_REQUEST_LANG_MAP[locale as string]) {
                replaceLocale(NEW_REQUEST_LANG_MAP[locale as string]);
            } else {
                initAppLogwebLoadMetrics();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const { asPath, query } = router;
        console.log('asPath->', asPath);
        console.log('query->', query);
        const SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY_DSN;
        const SENTRY_RELEASE = process.env.NEXT_PUBLIC_SENTRY_RELEASE;
        const SENTRY_ENV = process.env.NEXT_PUBLIC_SENTRY_ENV;

        const redirectPath: string = getRedirectPath(asPath, notFoundConfig);
        if (redirectPath) {
            // 404页面不需要初始化sentry
            console.log('notFoundConfig->page');
            return;
        }
        if (SENTRY_DSN && SENTRY_RELEASE) {
            try {
                Sentry.init({
                    dsn: SENTRY_DSN,
                    integrations: [new Sentry.BrowserTracing()],
                    tracesSampleRate: 1.0,
                    release: SENTRY_RELEASE,
                    environment: SENTRY_ENV,
                    ignoreErrors: ["undefined is not an object (evaluating 'window.webkit.messageHandlers')"],
                });
            } catch (err) {
                console.log(`init error, ${err}`);
            }
        }
    }, [router]);

    return (
        <>
            <div className="main-container">
                <StoreProvider>
                    <Component {...pageProps} />
                </StoreProvider>
            </div>
        </>
    );
}

EfApp.getInitialProps = async function getInitialProps(context: any) {
    return { ...(await NextApp.getInitialProps(context)) };
};

// @ts-ignore
export default appWithTranslation(EfApp);

export function reportWebVitals(metric: any) {
    const { name, value, label } = metric;

    if (label === 'web-vital') {
        const metricStorage = getLocalStorage('efmetric') || {};
        const data = typeof metricStorage === 'string' && isJSON(metricStorage) ? JSON.parse(metricStorage) : metricStorage;
        data[name] = value;
        setLocalStorage('efmetric', JSON.stringify(data));
    }
}
